import {
  Button,
  FileInput,
  Group,
  rem,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import * as icons from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { Header } from "@/components/Header";
import { TrainerRoutes, useNavigator } from "@/routers/routes";
import { getContentType } from "@/utils/content-types";
import { PreviewElement } from "../universal/FilePreview";

export const FileUpload: React.FC = () => {
  const { navigate } = useNavigator();
  const { folderId } = useParams<{ folderId: string }>();

  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  const fileUploadMutation = client.useMutation(
    "post",
    "/universal/file-upload",
  );
  const addToLibraryMutation = client.useMutation("post", "/universal/files/");

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      fileName: "",
      description: "",
    },
    enhanceGetInputProps: () => ({ disabled: !file }),
    validate: {
      fileName: isNotEmpty("File name is required"),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!file) {
      return;
    }
    const fileUpload = await fileUploadMutation.mutateAsync({});
    const formData = new FormData();

    Object.entries(fileUpload.params.fields).forEach(([k, v]) =>
      formData.append(k, v),
    );
    formData.append("file", file);

    await fetch(fileUpload.params.url, {
      method: "POST",
      body: formData,
    });

    await addToLibraryMutation.mutateAsync({
      body: {
        uploadId: fileUpload.id,
        originalName: file.name,
        name: values.fileName,
        description: values.description,
        type: getContentType(file.type),
        parentFolderId: folderId === "root" ? undefined : folderId,
        isPublic: false,
      },
    });

    if (folderId && folderId !== "root") {
      navigate({
        path: TrainerRoutes.LIBRARY.FOLDER,
        params: { folderId },
      });
    } else {
      navigate({ path: TrainerRoutes.LIBRARY.LIST });
    }
  };

  const onFileChanged = (event: File | null) => {
    setFile(event);
    if (!event) {
      setFileUrl(null);
      return;
    }
    form.reset();
    const fileNameWithoutExtension = event.name.replace(/\.[^/.]+$/, "");
    form.setValues({
      fileName: fileNameWithoutExtension,
    });
    const url = URL.createObjectURL(event);
    setFileUrl(url);
  };

  useEffect(
    () => () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    },
    [fileUrl],
  );

  return (
    <Stack>
      <Header title="Upload a file" />
      <FileInput
        onChange={(event) => onFileChanged(event)}
        leftSection={
          <icons.IconFile
            style={{ width: rem(18), height: rem(18) }}
            stroke={1.5}
          />
        }
        label="Upload a file"
        placeholder="Click here to pick a file"
        leftSectionPointerEvents="none"
      />
      {file && <Text>This looks like a {getContentType(file.type)} file.</Text>}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          withAsterisk
          label="File name"
          key={form.key("fileName")}
          {...form.getInputProps("fileName")}
        />
        <Textarea
          label="Description"
          key={form.key("description")}
          {...form.getInputProps("description")}
        />
        <Group justify="flex-end" mt="md">
          <Button
            fullWidth
            type="submit"
            loading={
              fileUploadMutation.isPending || addToLibraryMutation.isPending
            }
          >
            Save
          </Button>
        </Group>
      </form>
      {file && fileUrl && (
        <>
          <h3>Preview</h3>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <PreviewElement
              file={{
                previewUrl: fileUrl,
                type: getContentType(file.type),
                mimeType: file?.type,
              }}
            />
          </div>
        </>
      )}
    </Stack>
  );
};
